import './src/css/global.css';

import React from 'react';
import type { GatsbyBrowser } from 'gatsby';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import wrapWithProvider from '@/wrap-with-provider';
import { applyConsentDefaults } from '@/utils/consent';
import { initWebVitals, sendRouteChange } from '@/utils/gtm';
import { initErrorTelemetry } from '@/utils/errorTelemetry';

// Create a client Emotion cache that uses the SSR insertion point
function createEmotionCache() {
  const insertionPoint =
    typeof document !== 'undefined'
      ? (document.querySelector<HTMLMetaElement>('meta[name="emotion-insertion-point"]') ??
        undefined)
      : undefined;

  return createCache({ key: 'mui', prepend: true, insertionPoint });
}

const clientEmotionCache = createEmotionCache();

export const wrapRootElement: GatsbyBrowser['wrapRootElement'] = ({ element }) => (
  <CacheProvider value={clientEmotionCache}>{wrapWithProvider({ element })}</CacheProvider>
);

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
  applyConsentDefaults();
  // Register global failure telemetry (uncaught errors, rejections, chunk-load
  // failures) so otherwise-silent client failures reach the dataLayer.
  initErrorTelemetry();
  // Push default dataLayer value (preserves gatsby-plugin-google-tagmanager parity)
  if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ platform: 'gatsby' });
  }
  // Register web-vitals handlers (dynamically imported at idle time)
  initWebVitals();
};

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = () => {
  sendRouteChange();
};
